import { ChangeDetectorRef, Component, NgZone } from '@angular/core';
import { GigyaRaasComponentData } from '@spartacus/cdc/core';
import { CdcAuthFacade, CdcConfig, CdcJsService } from '@spartacus/cdc/root';
import { CdcFlowStateService } from '../../spartacus/services/cdc-flow-state.service';
import {
  AuthService,
  BaseSiteService,
  ClientTokenService,
  LanguageService,
  OccEndpointsService,
  OAuthLibWrapperService,
  WindowRef,
  RoutingService,
} from '@spartacus/core';
import { CmsComponentData } from '@spartacus/storefront';
import {
  Observable,
  distinctUntilChanged,
  tap,
  take,
  firstValueFrom,
} from 'rxjs';
import { filter } from 'rxjs/operators';
import { CdcService } from '../../spartacus/services/cdc.service';
import { Router, NavigationEnd } from '@angular/router';
import { UserInfoService } from '../../spartacus/services/user-info.service';
import { CommonApiService } from '../../spartacus/services/common-api.service';
import { CookieService } from 'ngx-cookie-service';
import { CdcTokenExchangeService } from '../../spartacus/services/cdc-token-exchange.service';

@Component({
  selector: 'app-custom-gigya-raas',
  templateUrl: './custom-gigya-raas.component.html',
  styleUrls: ['./custom-gigya-raas.component.scss'],
  standalone: false,
})
export class CustomGigyaRaasComponent {
  protected renderScreenSet = true;

  language$: Observable<string> | undefined;
  jsError$: Observable<boolean> | undefined;
  jsLoaded$: Observable<boolean> | undefined;

  currentRoute: string = '';
  setCurrentScreen: string = '';
  isUserLoggedIn: boolean = false;
  editProfile: boolean = false;
  apiErr: string = '';
  multipleSoldToPopup: boolean = false;
  currentSite: string = '';
  currUrl!: string | null;
  value: any;
  ukSplashPageData: any;
  cookieValue!: string;

  // Avoid binding Gigya handlers multiple times
  private gigyaHandlersBound = false;

  public constructor(
    public component: CmsComponentData<GigyaRaasComponentData>,
    private baseSiteService: BaseSiteService,
    private languageService: LanguageService,
    private cdcConfig: CdcConfig,
    private winRef: WindowRef,
    private cdcJSService: CdcJsService,
    private zone: NgZone,
    protected cdcAuth: CdcAuthFacade,
    private cdc: CdcService,
    private router: Router,
    public userInfoService: UserInfoService,
    private cd: ChangeDetectorRef,
    private authService: AuthService,                  // used to install user token
    private occEndpointsService: OccEndpointsService,
    private commonService: CommonApiService,
    private clientTokenService: ClientTokenService,
    private cookieService: CookieService,
    private oAuthLibWrapper: OAuthLibWrapperService,   // soft fallback
    private cdcTokenExchange: CdcTokenExchangeService,
    private cdcFlowState: CdcFlowStateService,
    private routingService: RoutingService,            // ✅ Spartacus routing
  ) {
    const url = this.router.url.split('?')[0].split('/');
    this.currentRoute = url[url.length - 1];
  }

  loadGigyaJs() {
    this.jsLoaded$ = this.cdcJSService.didLoad();
    this.jsError$ = this.cdc.didScriptFailToLoad();
    this.language$ = this.languageService.getActive().pipe(
      distinctUntilChanged(),
      tap(() => (this.renderScreenSet = true))
    );
  }

  ngOnInit(): void {
    this.currentSite = this.getCurrentBaseSite();

    this.userInfoService.userLoggedIn$.subscribe((res) => {
      this.isUserLoggedIn = res;

      // UK multiple sold-to UI toggle (kept intact)
      if (this.isUserLoggedIn && this.currentSite == 'seqirusb2b-uk') {
        this.userInfoService.fetchMultipleSoldToForUser().subscribe((data: any) => {
          if (data?.multipleSoldToData?.length > 1) {
            this.multipleSoldToPopup = true;
          }
          const temp = <HTMLElement>(
            document.getElementsByClassName('GigyaLoginPageTemplate')[1]?.querySelector('.BodyContent')
          );
          if (this.isUserLoggedIn && !this.multipleSoldToPopup) {
            if (temp) temp!.style.display = 'none';
          } else {
            if (temp) temp!.style.display = '';
          }
        });
      }
    });

    this.loadGigyaJs();
    this.showCookie();
  }

  ngAfterViewInit() {
    document.getElementById('login-container')?.addEventListener('click', this.handleLoginRedirect);
    setTimeout(() => this.showCookie(), 3000);
  }

  handleLoginRedirect = (event: Event): void => {
    const target = event.target as HTMLElement;

    if (target.matches('.changepasssuccess_loginbtn')) {
      this.router.navigate(['login']);
    }

    if (target.matches('#gigya-checkbox-remember')) {
      this.updateCookie();
    }

    if (target.matches('.resendemail_btn')) {
      setTimeout(() => {
        const profEmail = (window as any).profileEmail;
        localStorage.setItem('email', profEmail);
        if (profEmail) {
          this.clientTokenService.getClientToken().subscribe((res) => {
            if (res) {
              const token = res.access_token;
              this.passCalltoVerification(token);
            }
          });
        }
      }, 500);
    }
  };

  updateCookie(): void {
    const remember = this.cookieService.get('remember-me');
    const usernameInput = document.getElementById('username') as HTMLInputElement;
    const username = usernameInput ? usernameInput.value : '';
    if (!remember || remember !== username) {
      const inSixtyMinutes = new Date(new Date().getTime() + 60 * 60 * 1000);
      this.cookieService.set('remember-me', username, inSixtyMinutes, '/', '', true, 'None');
      this.cd.detectChanges();
    }
  }

  getCookie(name: string) {
    return this.cookieService.get(name);
  }

  showCookie() {
    this.cookieValue = this.getCookie('remember-me');
    this.checkRememberMe();
    this.fillUsernameField();
  }

  checkRememberMe() {
    const checkbox = document.getElementById('gigya-checkbox-remember') as HTMLInputElement;
    if (checkbox) checkbox.checked = this.cookieValue !== '';
  }

  fillUsernameField() {
    const usernameField = document.getElementById('username') as HTMLInputElement;
    if (usernameField) usernameField.value = this.cookieValue;
  }

  passCalltoVerification(token: string) {
    const endpoint = this.occEndpointsService.buildUrl(
      `Verify/VerifyProfileEmail?emailId=${localStorage.getItem('email')}`
    );
    const payload = {
      url: endpoint,
      token: token,
      contentType: 'text/xml',
      responseType: 'text',
    };
    this.commonService.getAPICallNoAuth(payload).subscribe({
      next: (res: any) => {
        if (res == 'success') {
          this.router.navigate(['/verificationsent']);
        } else {
          this.apiErr = res;
        }
        this.cd.detectChanges();
      },
      error: () => {},
    });
  }

  displayScreenSet(data: GigyaRaasComponentData, lang: string): void {
    if (this.renderScreenSet) {
      this.showScreenSet(data, lang);
    }
    this.renderScreenSet = false;
  }

  showScreenSet(data: GigyaRaasComponentData, lang: string) {
    // Keep your custom CDC site event handlers (no navigation)
    this.customRegisterEventHandlers();

    // Guard the flow state
    this.cdcFlowState.start();

    // ✅ Register onLogin ONCE (works for both / and /login)
    if (!this.gigyaHandlersBound) {
      (this.winRef.nativeWindow as any)?.['gigya']?.accounts?.addEventHandlers({
        onLogin: (ev: any) => {
          this.zone.run(() => this.handleCdcLogin(ev));
        },
        onError:   () => this.cdcFlowState.end(),
        onAborted: () => this.cdcFlowState.end(),
      });
      this.gigyaHandlersBound = true;
    }

    const isLogin = this.isLoginScreenSet(data);

    const customLangParams = {
      invalid_login_or_password:
        'The Email Address or Password you entered does not match our records. Please try again or click Forgot Password',
      please_enter_a_valid_code: 'Please enter a valid verification code (not your access code).',
    };

    (this.winRef.nativeWindow as any)?.['gigya']?.accounts?.showScreenSet({
      screenSet: data.screenSet,
      startScreen: data.startScreen,
      lang,
      ...(this.displayInEmbedMode(data) ? { containerID: data.containerID } : {}),
      ...(isLogin
        ? { sessionExpiration: this.getSessionExpirationValue(), customLang: customLangParams }
        : {
            onAfterSubmit: (...params: any[]) => {
              this.zone.run(() => {
                this.cdc.onProfileUpdateEventHandler(...params);

                // Immediately refresh the profile from backend and update local store so UI reflects changes
                this.userInfoService.getUKMyProfileData().pipe(take(1)).subscribe({
                  next: (profileData: any) => {
                    if (profileData) {
                      this.userInfoService.setProfileData(profileData);
                    }
                    // existing behaviour: close edit view and trigger any site-specific API
                    setTimeout(() => {
                      this.handleGigyaProfileEvt(false);
                      if (this.currentSite == 'seqirusb2b-uk') {
                        this.emailtriggerapi();
                      }
                    }, 300);
                    this.cd.detectChanges();
                  },
                  error: () => {
                    // fallback to previous behaviour even if refresh fails
                    setTimeout(() => {
                      this.handleGigyaProfileEvt(false);
                      if (this.currentSite == 'seqirusb2b-uk') {
                        this.emailtriggerapi();
                      }
                    }, 300);
                  }
                });
              });
            },
          }),
    });
  }

  // === Centralized CDC onLogin handling ===
  private async handleCdcLogin(ev: any) {
    try {
      const resp = ev?.response || ev || {};
      const { UID, UIDSignature, signatureTimestamp, id_token } = resp as any;

      if (!UID || !UIDSignature || !signatureTimestamp || !id_token) {
        console.warn('[CDC] Missing required fields in onLogin response');
        return;
      }

      // 🔄 Reset per-login state to avoid cross-login leakage
      try { sessionStorage.removeItem('postLoginTarget'); } catch {}
      this.multipleSoldToPopup = false;

      // 1) Resolve baseSite reliably (await)
      const baseSite = await this.getCurrentBaseSiteAsync();

      // 2) CDC → Commerce: exchange custom grant
      const tokenResp = await this.cdcTokenExchange.exchangeCustomGrant({
        UID,
        UIDSignature,
        signatureTimestamp,
        id_token,
        baseSite,
      });
      const accessToken: string | undefined = tokenResp?.access_token;
      if (!accessToken) {
        throw new Error('CDC token exchange returned no access_token');
      }

      // 3) Install Commerce user token
      const maybeLoginWithToken = (this.authService as any)?.loginWithToken;
      if (typeof maybeLoginWithToken === 'function') {
        const ret = maybeLoginWithToken.call(this.authService, accessToken);
        if (ret?.subscribe) {
          await firstValueFrom(ret);
        }
      } else {
        // Older Spartacus minors: attempt OAuth refresh
        try {
          await (this.oAuthLibWrapper as any)?.refreshUserToken?.();
        } catch { /* non-fatal */ }
      }

      // 4) Wait until Spartacus confirms login
      await firstValueFrom(
        this.authService.isUserLoggedIn().pipe(
          filter((v) => !!v),
          take(1)
        )
      );

      // 5) Redirect (honors postLoginTarget; US/UK rules thereafter)
      await this.redirectToMyAccountStrong();

      this.cd.detectChanges();
    } catch (err) {
      console.error('CDC login/token exchange failed', err);
    } finally {
      this.cdcFlowState.end();
    }
  }

  /** tiny delay helper */
  private sleep(ms: number) {
    return new Promise((r) => setTimeout(r, ms));
  }

  /** Wait briefly for 'postLoginTarget' to be set by user-info.service (to avoid race). */
  private async waitForPostLoginTarget(timeoutMs = 200): Promise<string | null> {
    try {
      const start = Date.now();
      while (Date.now() - start < timeoutMs) {
        const v = (typeof window !== 'undefined') ? sessionStorage.getItem('postLoginTarget') : null;
        if (v) return v;
        await this.sleep(25);
      }
      return (typeof window !== 'undefined') ? sessionStorage.getItem('postLoginTarget') : null;
    } catch {
      return null;
    }
  }

  /** Check MSTL for current user (defensive against shape) */
  private async checkNeedsMstlPopup(): Promise<boolean> {
    try {
      const raw = await firstValueFrom(
        this.userInfoService.fetchMultipleSoldToForUser().pipe(take(1))
      );
      const anyRaw: any = raw as any;
      const list: any[] =
        (anyRaw && Array.isArray(anyRaw.multipleSoldToData) && anyRaw.multipleSoldToData) ||
        (Array.isArray(anyRaw) ? anyRaw : []);
      return Array.isArray(list) && list.length > 1;
    } catch {
      return false;
    }
  }

  /** Poll MSTL for a short, bounded window to let new user/session settle */
  private async waitForMstlPopupDecision(totalMs = 1200, stepMs = 200): Promise<boolean> {
    const start = Date.now();
    do {
      const needs = await this.checkNeedsMstlPopup();
      if (needs) return true;
      await this.sleep(stepMs);
    } while (Date.now() - start < totalMs);
    return false;
  }

  /**
   * ✅ Honors 'postLoginTarget' (set for non-webEnabled) BEFORE any default behavior.
   * ✅ UK (seqirusb2b-uk): SPA-only navigation, but defer to Multiple Sold-To popup:
   *    - Poll MSTL briefly; if needed → do nothing (popup will navigate after Select).
   *    - Else SPA to myAccount.
   * ✅ US (seqirusb2b-flu360): hard reload on homepage/first-login; SPA otherwise.
   */
  private async redirectToMyAccountStrong() {
    // 0) 🔒 If user-info.service set an explicit post-login target, honor it first (race-proof)
    const explicit = await this.waitForPostLoginTarget(200);
    if (explicit) {
      const targetPath = this.buildContextAwarePath(
        explicit.startsWith('/') ? explicit : `/${explicit}`
      );
      try { sessionStorage.removeItem('postLoginTarget'); } catch {}
      this.router.navigateByUrl(targetPath, { replaceUrl: true });
      return;
    }

    // 1) 🇬🇧 UK → SPA-only, but first ensure MSTL popup decision is fresh (after user switch)
    if (this.currentSite === 'seqirusb2b-uk') {
      // small initial delay then a short polling window for a stable response
      await this.sleep(60);
      const needsPopup = await this.waitForMstlPopupDecision(1200, 200);

      if (needsPopup) {
        // ✅ Do nothing → existing popup flow takes over and navigates after Select
        return;
      }

      // No popup needed → SPA to myAccount
      this.routingService.go({ cxRoute: 'myAccount' }, { replaceUrl: true });
      return;
    }

    // 2) 🇺🇸 US → keep your hard reload logic
    if (this.isSemanticHomepage()) {
      const targetPath = this.buildContextAwarePath('/my-account');
      const origin = (typeof window !== 'undefined') ? window.location.origin : '';
      window.location.replace(origin + targetPath); // replace avoids back to pre-login page
      return;
    }

    if (this.shouldHardRedirectFromLoginOnce()) {
      const targetPath = this.buildContextAwarePath('/my-account');
      const origin = (typeof window !== 'undefined') ? window.location.origin : '';
      sessionStorage.setItem('loginHardRedirectDone', '1');
      window.location.replace(origin + targetPath);
      return;
    }

    // 3) SPA path (works well for subsequent /login navigations and other pages)
    const go = () => this.routingService.go({ cxRoute: 'myAccount' }, { replaceUrl: true });
    queueMicrotask(go);
    this.router.events
      .pipe(filter(e => e instanceof NavigationEnd), take(1))
      .subscribe(() => go());
    setTimeout(go, 0);
  }

  // >>> Kept for potential reuse elsewhere (not used by main flow)
  private async completeCdcLoginAfterExchange(exchangeResp: any) {
    const accessToken: string = exchangeResp?.access_token;
    if (!accessToken) {
      throw new Error('CDC token exchange returned no access_token');
    }

    const maybeLoginWithToken = (this.authService as any)?.loginWithToken;
    if (typeof maybeLoginWithToken === 'function') {
      const ret = maybeLoginWithToken.call(this.authService, accessToken);
      if (ret?.subscribe) {
        await firstValueFrom(ret);
      }
    } else {
      try {
        await (this.oAuthLibWrapper as any)?.refreshUserToken?.();
      } catch {
        // ignore
      }
    }
    this.router.navigateByUrl('/', { replaceUrl: true });
  }

  protected isLoginScreenSet(data: GigyaRaasComponentData): boolean {
    const profileEditScreen: boolean = data.profileEdit === 'true' ? true : false;
    return !profileEditScreen;
  }

  protected getSessionExpirationValue(): number {
    if (this.cdcConfig?.cdc !== undefined) {
      const filteredConfigs: any = this.cdcConfig.cdc.filter(
        (conf) => conf.baseSite === this.getCurrentBaseSite()
      );
      if (filteredConfigs && filteredConfigs.length > 0) {
        return filteredConfigs[0].sessionExpiration;
      }
    }
    return 3600;
  }

  // ⚠️ Synchronous utility used elsewhere in your codebase
  getCurrentBaseSite(): string {
    let baseSite: string = '';
    this.baseSiteService.getActive().pipe(take(1)).subscribe((data) => (baseSite = data));
    return baseSite;
  }

  // ✅ Robust async version for login/token exchange
  private async getCurrentBaseSiteAsync(): Promise<string> {
    return await firstValueFrom(this.baseSiteService.getActive().pipe(take(1)));
  }

  displayInEmbedMode(data: GigyaRaasComponentData): boolean {
    const embedValue: boolean = data.embed === 'true';
    return embedValue && !!data.containerID && data.containerID.length > 0;
  }

  customRegisterEventHandlers() {
    const baseSite = this.getCurrentBaseSite();
    if (baseSite) {
      this.cdc.customAddCdcEventHandlers(baseSite);
    }
  }

  handleGigyaProfileEvt(evt: boolean) {
    this.editProfile = evt;
    const temp = <HTMLElement>document.getElementsByClassName('BodyContent')[0];
    if (this.editProfile) {
      if (temp) temp.classList.add('profile_view');
    } else {
      if (temp) temp.classList.remove('profile_view');
    }
    this.cd.detectChanges();
  }

  toggleEditValue(evt: any): void {
    this.handleGigyaProfileEvt(evt);
  }

  emailtriggerapi() {
    this.userInfoService.emailapi().subscribe(() => {});
  }

  /** Detect if current route is the site’s homepage (semantic “/”, with any site/lang/currency prefix). */
  private isSemanticHomepage(): boolean {
    const path = (typeof window !== 'undefined')
      ? window.location.pathname
      : this.router.url.split('?')[0];

    const segs = path.split('?')[0].split('#')[0].split('/').filter(Boolean);
    if (segs.length >= 3) {
      const [, s2, s3] = segs;
      const isLang = /^[a-z]{2}(?:_[A-Z]{2})?$/.test(s2);
      const isCurr = /^[A-Z]{3}$/.test(s2);
      const isLang3 = /^[a-z]{2}(?:_[A-Z]{2})?$/.test(s3);
      const isCurr3 = /^[A-Z]{3}$/.test(s3);
      if ((isLang && isCurr3) || (isCurr && isLang3)) {
        // Homepage if no extra segments OR explicit "homepage"
        const rest = segs.slice(3);
        return rest.length === 0 || rest.join('/') === 'homepage';
      }
    }
    // Fallbacks: "/<site>" or "/<site>/homepage" or root "/"
    if (segs.length >= 1) {
      return segs.length === 1 || segs.slice(1).join('/') === 'homepage';
    }
    return true;
  }

  /** Detect if current route is the semantic “/login” (with any site/lang/currency prefix). */
  private isSemanticLogin(): boolean {
    const path = (typeof window !== 'undefined')
      ? window.location.pathname
      : this.router.url.split('?')[0];

    const segs = path.split('?')[0].split('#')[0].split('/').filter(Boolean);
    if (segs.length >= 3) {
      const [, s2, s3] = segs;
      const isLang = /^[a-z]{2}(?:_[A-Z]{2})?$/.test(s2);
      const isCurr = /^[A-Z]{3}$/.test(s2);
      const isLang3 = /^[a-z]{2}(?:_[A-Z]{2})?$/.test(s3);
      const isCurr3 = /^[A-Z]{3}$/.test(s3);
      if ((isLang && isCurr3) || (isCurr && isLang3)) {
        const rest = segs.slice(3);
        return rest.length === 1 && rest[0] === 'login';
      }
    }
    // Fallbacks: "/<site>/login" or "/login"
    if (segs.length >= 2) {
      return segs.slice(1).join('/') === 'login';
    }
    return segs.length === 1 && segs[0] === 'login';
  }

  /** One-time hard redirect rule for first successful login from /login after cache/cookie clear. */
  private shouldHardRedirectFromLoginOnce(): boolean {
    if (sessionStorage.getItem('loginHardRedirectDone') === '1') {
      return false;
    }
    return this.isSemanticLogin();
  }

  /** Return true if the first path segment is a known Spartacus baseSite. */
  private isKnownBaseSite(seg?: string): boolean {
    if (!seg) return false;
    const known = ['seqirusb2b-flu360', 'seqirusb2b-uk'];
    return known.includes(seg) || seg === this.currentSite;
  }

  private isLang(seg?: string): boolean {
    return !!seg && /^[a-z]{2}(?:_[A-Z]{2})?$/.test(seg);
  }
  private isCurr(seg?: string): boolean {
    return !!seg && /^[A-Z]{3}$/.test(seg);
  }

  /** Build a context-aware path for a semantic target like “/my-account”.
   * NOTE: For UK we DO NOT call this in redirectToMyAccountStrong(); UK uses SPA only.
   */
  private buildContextAwarePath(targetSemantic: string = '/my-account'): string {
    const path = (typeof window !== 'undefined')
      ? window.location.pathname
      : this.router.url.split('?')[0];

    const segs = path.split('?')[0].split('#')[0].split('/').filter(Boolean);
    const target = targetSemantic.replace(/^\//, ''); // 'my-account'

    if (segs.length === 0) return `/${target}`;

    const [s1, s2, s3] = segs;

    // If first segment is a known baseSite → preserve site (and optionally lang/currency)
    if (this.isKnownBaseSite(s1)) {
      if (this.isLang(s2) && this.isCurr(s3)) return ['', s1, s2, s3, target].join('/');
      if (this.isCurr(s2) && this.isLang(s3)) return ['', s1, s2, s3, target].join('/');
      return ['', s1, target].join('/');
    }

    // Try to preserve pure currency/lang prefix if present (future-proof)
    if (this.isCurr(s1) && this.isLang(s2)) return ['', s1, s2, target].join('/');
    if (this.isLang(s1) && this.isCurr(s2)) return ['', s1, s2, target].join('/');

    // Default: domain-root "/my-account"
    return `/${target}`;
  }
}